import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "@repo/i18n";
import { Button, Skeleton } from "@repo/components";
import { css } from "@repo/styled-system/css";
import { ApiError } from "@repo/api-client";
import { useVenue } from "../shell/VenueContext";
import { useMinuteClock } from "../shell/useMinuteClock";
import { useServices } from "../bookings/queries";
import { FloorMap, type ArmedSeat } from "./FloorMap";
import { WalkInForm } from "./WalkInForm";
import type { LiveFloorData, LiveRow, LiveTable, LiveZone } from "./types";
import {
  todayServiceDay,
  useArrive,
  useDepart,
  useFloor,
  useFloorGeometry,
  useMove,
  useNoShow,
  useSeat,
  useTableReady,
  useTimeline,
} from "./queries";
import {
  cleaningElapsedMin,
  isLate,
  minutesLate,
  occupancyMeter,
  tableStateTint,
  type OccupancyMeter,
} from "./lateness";
import {
  badgeBase,
  liveStyles as s,
  stateBadgeClass,
  stateDotClass,
  tableTint,
  type Tint,
} from "./styles";

type View = "PLAN" | "LIST";
const VIEW_KEY = "live:view";

/** Read the per-viewer floor view, tolerating a blocked/empty localStorage (private window, etc.). */
function readStoredView(): View | null {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === "PLAN" || v === "LIST" ? v : null;
  } catch {
    return null;
  }
}
function writeStoredView(v: View): void {
  try {
    localStorage.setItem(VIEW_KEY, v);
  } catch {
    /* ignore — the choice just won't persist this session */
  }
}

/**
 * The live-service surface (`/v/:venueId/live`, "Service en direct"): the WAITER's room organiser as
 * guests arrive (reserved + walk-in). The today timeline + the live floor with one-tap
 * arrive/seat/depart/no-show, a prominent walk-in, and a Plan / Liste toggle over the floor. Table
 * states are DERIVED/RECORDED server-side (R1); the client only renders + taps. A 409 (two devices,
 * §D.3) shows the "already updated" notice and refetches — never last-write-wins.
 */
export function LiveServicePage() {
  const { t } = useTranslation();
  const { venueId, venue } = useVenue();
  // So the late + stuck-cleaning badges tick without a refetch (R4).
  const nowSec = useMinuteClock();
  const serviceDay = useMemo(() => todayServiceDay(venue.timeZone), [venue.timeZone]);

  const timeline = useTimeline(venueId, serviceDay);
  const floor = useFloor(venueId);
  const plan = useFloorGeometry(venueId);
  const services = useServices(venueId);

  // A drawn plan with live tables → the spatial FloorMap (Plan). A CAPACITY_ONLY venue, an empty plan,
  // or a still-loading geometry has no spatial map, so it defaults to (and stays on) the Liste view.
  const spatial =
    plan.data?.mode === "FLOOR_PLAN" &&
    (plan.data?.tables?.some((tb) => !tb.retired) ?? false) &&
    (floor.data?.tables?.length ?? 0) > 0;

  const [storedView, setStoredView] = useState<View | null>(() => readStoredView());
  // Non-spatial venues have no Plan to show → always Liste; spatial venues honour the stored choice.
  const view: View = spatial ? (storedView ?? "PLAN") : "LIST";
  const selectView = (v: View) => {
    setStoredView(v);
    writeStoredView(v);
  };

  const [notice, setNotice] = useState<string | null>(null);
  // The booking currently armed for (re)seating on the map/list (tap a free table to commit).
  const [armed, setArmed] = useState<ArmedSeat | null>(null);
  // The booking whose no-show is armed for an INLINE confirm (replaces the native window.confirm).
  const [confirmNoShow, setConfirmNoShow] = useState<string | null>(null);
  const onError = (err: unknown) => {
    // A 409 means another device moved first; drop any armed pick so the operator re-taps against the
    // authoritative state the refetch is about to land, rather than re-firing a stale version.
    setArmed(null);
    if (err instanceof ApiError && err.status === 409) setNotice(t("live.notice.alreadyUpdated"));
    else setNotice(t("live.notice.actionFailed"));
  };
  const clearNotice = () => setNotice(null);

  const arrive = useArrive(venueId);
  const seat = useSeat(venueId);
  const move = useMove(venueId);
  const depart = useDepart(venueId);
  const noShow = useNoShow(venueId);
  const tableReady = useTableReady(venueId);
  // One in-flight action at a time: on a laggy tablet a double-tap fires a stale version → 409 →
  // "already updated" for something that DID work. Disable the row actions while any mutation runs.
  const busy = arrive.isPending || seat.isPending || depart.isPending || noShow.isPending;

  const firstAvailableTable = floor.data?.tables?.find((tbl) => tbl.state === "AVAILABLE")?.tableId;
  const walkInServiceId = timeline.data?.[0]?.serviceId ?? services.data?.[0]?.id;

  const doArrive = (row: LiveRow) => {
    clearNotice();
    arrive.mutate({ bookingId: row.bookingId, version: row.version }, { onError });
  };
  const doSeat = (row: LiveRow) => {
    clearNotice();
    // If any table is free, arm the booking and let the operator tap the exact table (Plan OR Liste).
    // With no free table at all (pure capacity/counter), keep the one-tap counter seat.
    if (firstAvailableTable) {
      setArmed({ bookingId: row.bookingId, version: row.version, label: guestName(row) });
      return;
    }
    seat.mutate(
      { bookingId: row.bookingId, body: { version: row.version, tableIds: [] } },
      { onError },
    );
  };

  // Commit the armed booking onto the tapped table. Arming from an occupied table (fromTableId set) is a
  // reassign → the `move` command (keeps the party SEATED, frees the old table); a fresh arm is a `seat`
  // (CONFIRMED → SEATED). Both CAS on the armed version and 409 into the "already updated" notice.
  const seatArmedAt = (tableId: string) => {
    if (armed === null) return;
    clearNotice();
    const mutation = armed.fromTableId ? move : seat;
    mutation.mutate(
      { bookingId: armed.bookingId, body: { version: armed.version, tableIds: [tableId] } },
      { onError, onSuccess: () => setArmed(null) },
    );
  };

  // Pick up an already-seated table for a reassign: resolve its backing booking's live row (for the
  // current version) and arm it, tagged with the source table so the map marks where it came from.
  const pickTable = (tbl: LiveTable) => {
    const row = timeline.data?.find((r) => r.bookingId === tbl.backingBookingId);
    if (!row) return;
    clearNotice();
    setArmed({
      bookingId: row.bookingId,
      version: row.version,
      label: guestName(row),
      fromTableId: tbl.tableId,
    });
  };
  const markReady = (tableId: string) => {
    clearNotice();
    tableReady.mutate({ tableId }, { onError });
  };
  const doDepart = (row: LiveRow) => {
    clearNotice();
    depart.mutate({ bookingId: row.bookingId, version: row.version }, { onError });
  };
  const doNoShow = (row: LiveRow) => {
    // No-show is punitive (feeds the guest no-show counter + the no-show-fee flow), so it fires only
    // after the INLINE confirm below — a single stray tap on a tablet never triggers it.
    clearNotice();
    setConfirmNoShow(null);
    noShow.mutate({ bookingId: row.bookingId, version: row.version }, { onError });
  };

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div>
          <h1 className={s.title}>{t("live.title")}</h1>
          <p className={s.subtitle}>{venue.displayName}</p>
        </div>
        <Link to={`/v/${venueId}/door`} className={css({ textDecoration: "underline" })}>
          {t("live.openDoor")}
        </Link>
      </header>

      {notice && (
        <output className={s.alert}>
          {notice}
          <Button
            size="2xs"
            variant="plain"
            onClick={clearNotice}
            aria-label={t("live.notice.dismiss")}
          >
            ×
          </Button>
        </output>
      )}

      <div className={s.toolbar}>
        <WalkInForm venueId={venueId} serviceId={walkInServiceId} prominent />
        {spatial && (
          <div className={s.segmented} aria-label={t("live.view.label")}>
            <button
              type="button"
              className={`${s.segment} ${view === "PLAN" ? s.segmentActive : ""}`}
              aria-pressed={view === "PLAN"}
              onClick={() => selectView("PLAN")}
            >
              {t("live.view.plan")}
            </button>
            <button
              type="button"
              className={`${s.segment} ${view === "LIST" ? s.segmentActive : ""}`}
              aria-pressed={view === "LIST"}
              onClick={() => selectView("LIST")}
            >
              {t("live.view.list")}
            </button>
          </div>
        )}
      </div>

      <Occupancy floor={floor.data} />

      <section aria-labelledby="floor-heading" className={s.section}>
        <h2 id="floor-heading" className={s.sectionTitle}>
          {t("live.floor.title")}
        </h2>

        {armed && (
          <output className={s.armBar}>
            <span>
              {t(armed.fromTableId ? "live.seat.armReassign" : "live.seat.armSeat", {
                guest: armed.label,
              })}
            </span>
            <Button size="sm" variant="outline" onClick={() => setArmed(null)}>
              {t("live.seat.cancel")}
            </Button>
          </output>
        )}

        {floor.isError && (
          <p role="alert" className={css({ color: "fg.error" })}>
            {t("live.loadError")}
          </p>
        )}
        {floor.isLoading && <Skeleton height="40" />}

        {floor.data &&
          (view === "PLAN" && spatial && plan.data ? (
            <FloorMap
              plan={plan.data}
              tables={floor.data.tables}
              rows={timeline.data ?? []}
              armed={armed}
              nowSec={nowSec}
              timeZone={venue.timeZone}
              onSeatTable={seatArmedAt}
              onPickTable={pickTable}
              onTableReady={markReady}
            />
          ) : (
            <ListFloor
              floor={floor.data}
              rows={timeline.data ?? []}
              armed={armed}
              nowSec={nowSec}
              timeZone={venue.timeZone}
              onSeatTable={seatArmedAt}
              onPickTable={pickTable}
              onTableReady={markReady}
            />
          ))}
      </section>

      <section aria-labelledby="timeline-heading" className={s.section}>
        <h2 id="timeline-heading" className={s.sectionTitle}>
          {t("live.timeline.title")}
        </h2>
        {timeline.isLoading && <p className={s.empty}>{t("live.loading")}</p>}
        {timeline.isError && (
          <p role="alert" className={css({ color: "fg.error" })}>
            {t("live.loadError")}
          </p>
        )}
        {timeline.data && timeline.data.length === 0 && (
          <p className={s.empty}>{t("live.timeline.empty")}</p>
        )}
        <ul className={s.rows}>
          {(timeline.data ?? []).map((row) => (
            <li key={row.bookingId} className={s.row}>
              <div className={s.rowMain}>
                <span className={s.rowTime}>{formatTime(row.startSec, venue.timeZone)}</span>
                <span className={s.rowGuest}>{guestName(row)}</span>
                <span className={s.rowMeta}>
                  {t("live.party", { n: row.partySize })}
                  {" · "}
                  {t(`live.statusName.${row.status}`)}
                </span>
                {row.pendingAssignment && (
                  <span className={s.pending}>{t("live.pendingAssignment")}</span>
                )}
                {isLate(row, nowSec) && (
                  <span className={s.late}>
                    {t("live.late", { min: minutesLate(row, nowSec) })}
                  </span>
                )}
                {row.allergyNote && <span className={s.allergy}>{t("live.allergy")}</span>}
              </div>
              <div className={s.rowActions}>
                {row.status === "CONFIRMED" &&
                  (confirmNoShow === row.bookingId ? (
                    <div className={s.confirmRow}>
                      <span className={s.confirmText}>
                        {t("live.confirm.noShow", { guest: guestName(row) })}
                      </span>
                      <Button
                        size="sm"
                        variant="solid"
                        colorPalette="red"
                        disabled={busy}
                        onClick={() => doNoShow(row)}
                      >
                        {t("live.confirm.proceed")}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmNoShow(null)}>
                        {t("live.confirm.dismiss")}
                      </Button>
                    </div>
                  ) : (
                    <>
                      {row.arrivedAtSec === null && (
                        <Button variant="outline" disabled={busy} onClick={() => doArrive(row)}>
                          {t("live.action.arrive")}
                        </Button>
                      )}
                      <Button variant="solid" disabled={busy} onClick={() => doSeat(row)}>
                        {t("live.action.seat")}
                      </Button>
                      <Button
                        variant="outline"
                        colorPalette="red"
                        disabled={busy}
                        onClick={() => {
                          clearNotice();
                          setConfirmNoShow(row.bookingId);
                        }}
                      >
                        {t("live.action.noShow")}
                      </Button>
                    </>
                  ))}
                {row.status === "SEATED" && (
                  <Button variant="solid" disabled={busy} onClick={() => doDepart(row)}>
                    {t("live.action.depart")}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/** A table's state badge — token colour + a shape dot + the state word (never hue alone; RGAA/WCAG). */
function StateBadge({ tint }: { tint: Tint }) {
  const { t } = useTranslation();
  return (
    <span className={`${badgeBase} ${stateBadgeClass[tint]}`} data-testid="live-table-state">
      <span aria-hidden className={`${css({ fontSize: "2xs" })} ${stateDotClass[tint]}`}>
        ●
      </span>
      {t(`live.state.${tint}`)}
    </span>
  );
}

/**
 * The compact LISTE floor: every table grouped by zone with its state + capacity and the SAME tap
 * actions as the Plan (seat-when-armed, pick-occupied for a reassign, mark-ready on cleaning). The
 * primary floor for CAPACITY_ONLY / empty / no-entitlement venues where there is no drawn map.
 */
function ListFloor({
  floor,
  rows,
  armed,
  nowSec,
  timeZone,
  onSeatTable,
  onPickTable,
  onTableReady,
}: {
  floor: LiveFloorData;
  rows: LiveRow[];
  armed: ArmedSeat | null;
  nowSec: number;
  timeZone: string;
  onSeatTable: (tableId: string) => void;
  onPickTable: (table: LiveTable) => void;
  onTableReady: (tableId: string) => void;
}) {
  const { t } = useTranslation();
  const rowById = useMemo(() => new Map(rows.map((r) => [r.bookingId, r])), [rows]);

  if (floor.tables.length === 0) {
    return <p className={s.empty}>{t("live.floor.noTables")}</p>;
  }

  // Zone order follows the floor read; any table whose zone is unknown lands in a trailing "other" lane.
  const zoneName = new Map(floor.zones.map((z) => [z.zoneId, z.name] as const));
  const order: { id: string; name: string }[] = floor.zones.map((z: LiveZone) => ({
    id: z.zoneId,
    name: z.name,
  }));
  const grouped = new Map<string, LiveTable[]>();
  for (const tb of floor.tables) {
    const key = zoneName.has(tb.zoneId) ? tb.zoneId : "__other__";
    const list = grouped.get(key) ?? [];
    list.push(tb);
    grouped.set(key, list);
  }
  if (grouped.has("__other__")) order.push({ id: "__other__", name: "" });

  return (
    <div>
      {order.map((zone) => {
        const zt = grouped.get(zone.id);
        if (!zt || zt.length === 0) return null;
        return (
          <div key={zone.id} className={s.listZone}>
            {zone.name && <div className={s.listZoneHead}>{zone.name}</div>}
            <div className={s.listGrid}>
              {zt.map((tb) => (
                <ListTable
                  key={tb.tableId}
                  tb={tb}
                  occupant={tb.backingBookingId ? (rowById.get(tb.backingBookingId) ?? null) : null}
                  armed={armed}
                  nowSec={nowSec}
                  timeZone={timeZone}
                  onSeatTable={onSeatTable}
                  onPickTable={onPickTable}
                  onTableReady={onTableReady}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ListTable({
  tb,
  occupant,
  armed,
  nowSec,
  timeZone,
  onSeatTable,
  onPickTable,
  onTableReady,
}: {
  tb: LiveTable;
  occupant: LiveRow | null;
  armed: ArmedSeat | null;
  nowSec: number;
  timeZone: string;
  onSeatTable: (tableId: string) => void;
  onPickTable: (table: LiveTable) => void;
  onTableReady: (tableId: string) => void;
}) {
  const { t } = useTranslation();
  const tint = tableStateTint(tb.state);
  const isTarget = armed !== null && tb.state === "AVAILABLE";
  // Same modality as the Plan: armed → only AVAILABLE tables are targets; idle → AVAILABLE is inert.
  const disabled = armed !== null ? !isTarget : tb.state === "AVAILABLE";

  const onClick = () => {
    if (armed !== null) {
      if (isTarget) onSeatTable(tb.tableId);
      return;
    }
    if (tb.state === "CLEANING") onTableReady(tb.tableId);
    else if (tb.backingBookingId) onPickTable(tb);
  };

  const stateWord = t(`live.state.${tint}`);
  const label = isTarget
    ? t("live.floor.seatHere", { label: tb.label })
    : t("live.floor.tableState", { label: tb.label, state: stateWord });
  const showParty = tint === "occupied" || tint === "reserved";

  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`${tableTint[tint]} ${s.listRow} ${isTarget ? s.listRowTarget : ""}`}
    >
      <span className={s.listMain}>
        <span className={s.listLabel}>{tb.label}</span>
        <span className={s.listMeta}>
          {showParty && occupant
            ? t("live.party", { n: occupant.partySize })
            : t("live.floor.capacity", { max: tb.maxCovers })}
          {showParty && tb.sinceSec !== null && ` · ${formatTime(tb.sinceSec, timeZone)}`}
          {tb.stuck &&
            ` · ${t("live.floor.stuck", { min: cleaningElapsedMin(tb.sinceSec, nowSec) })}`}
        </span>
      </span>
      <StateBadge tint={tint} />
    </button>
  );
}

function Occupancy({ floor }: { floor: LiveFloorData | undefined }) {
  const { t } = useTranslation();
  const zones = (floor?.zones ?? []).filter(
    (z) => z.standing || z.legalCapacity !== null || z.capacity !== null,
  );
  if (zones.length === 0) return null;
  return (
    <section aria-labelledby="occupancy-heading" className={css({ mb: "4" })}>
      <h2 id="occupancy-heading" className={css({ srOnly: true })}>
        {t("live.occupancy.title")}
      </h2>
      <div className={s.meters}>
        {zones.map((z) => {
          const m: OccupancyMeter = occupancyMeter(z);
          return (
            <div key={z.zoneId} className={s.meter}>
              <div className={s.meterHead}>
                <span>{z.name}</span>
                <span className={m.over ? s.meterOver : m.nearLimit ? s.meterWarn : undefined}>
                  {m.ceiling !== null ? `${m.occupied} / ${m.ceiling}` : String(m.occupied)}
                  {m.pct !== null ? ` (${m.pct}%)` : ""}
                </span>
              </div>
              {/* Decorative bar — the numeric occupancy value above is the accessible source of truth. */}
              <div className={s.meterTrack} aria-hidden="true">
                <div
                  className={`${s.meterFill} ${m.over ? s.meterFillOver : m.nearLimit ? s.meterFillWarn : ""}`}
                  style={{ width: `${Math.round(m.ratio * 100)}%` }}
                />
              </div>
              {m.over && <span className={s.meterOver}>{t("live.occupancy.over")}</span>}
              {!m.over && m.nearLimit && (
                <span className={s.meterWarn}>{t("live.occupancy.near")}</span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function guestName(row: LiveRow): string {
  return [row.guestGivenName, row.guestFamilyName].filter(Boolean).join(" ") || "—";
}

function formatTime(startSec: number, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
    }).format(startSec * 1000);
  } catch {
    return new Date(startSec * 1000).toISOString().slice(11, 16);
  }
}
